import React, { Component, createRef } from 'react';
import { withApollo, graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { compose, mapProps } from 'recompose';
import InfiniteScroll from 'react-infinite-scroller';

import _ from 'lodash';
import PropTypes from 'prop-types';

import Category from 'components/threepp/assessment/Category/Category';
import Loader from 'components/common/Loader/Spinner/Spinner';
import { getQueryFilterVariables } from 'selectors/assessmentFilters/selector';
import { listFieldsForCategory, setAssigneesForField } from 'graphql/operations';
import { getObjValue, isDifferent } from 'utils/functions';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';
import './Questions.scss';
import { isAssessmentSubmitted } from 'selectors/assessment/selector';

export class Questions extends Component {
  static propTypes = {
    // TODO: Fix this...
    /* eslint-disable react/forbid-prop-types */
    fieldsFilter: PropTypes.object,
    t: PropTypes.func.isRequired,
    client: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    assessment: PropTypes.object.isRequired,
    assessmentParticipants: PropTypes.array.isRequired,
    /* eslint-enable react/forbid-prop-types */
    incrementAttachmentUploadCount: PropTypes.func.isRequired,
    decrementAttachmentUploadCount: PropTypes.func.isRequired,
    isNewRecord: PropTypes.bool
  };

  static defaultProps = {
    fieldsFilter: null,
    isNewRecord: false
  };

  constructor(props) {
    super(props);
    this.state = this.initState();
    this.categoryRefs = props.categories.map(() => createRef());
  }

  componentDidUpdate(prevProps) {
    const { fieldsFilter, assessmentParticipants, selectedCategoryOrder, categories } = this.props;
    const { queryVariables } = this.state;
    let { currentCategory, requestMoreFields } = this.state;
    // Re-init state to trigger data fetch with new filters
    const changed = isDifferent(prevProps.fieldsFilter, fieldsFilter);
    const selectedCategoryChanged = prevProps.selectedCategoryOrder !== selectedCategoryOrder;
    if (changed) {
      // TODO: Figure this next issue out as it's a big code smell...
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.initState());
      return;
    }
    if (selectedCategoryChanged && selectedCategoryOrder >= currentCategory?.order) {
      const categoriesToLoad = currentCategory ? selectedCategoryOrder - currentCategory?.order : 0;
      currentCategory = categories.find(c => c.order === selectedCategoryOrder);
      requestMoreFields = true;
      // TODO: Figure this next issue out as it's a big code smell...
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        currentCategory,
        requestMoreFields,
        categoriesToLoad
      });
      EventEmitter.dispatch(EventEmitterTypes.SHOW_LOADER, { showLoader: true });
      this.autoLoad();
      return;
    }

    // Remove assignees from local data store
    const newParticipants = assessmentParticipants;
    const prevParticipants = prevProps.assessmentParticipants;

    if (newParticipants.length < prevParticipants.length) {
      const users = _.differenceBy(prevParticipants, newParticipants, 'email');
      const email = users.map(user => user.email);
      this.addRemoveQuestionParticipants(queryVariables, [], email);
    }
  }

  initState = () => {
    const { categories } = this.props;
    const hasMore = categories.length > 0;
    const currentCategory = hasMore ? categories[0] : null;
    return {
      currentCategory,
      requestMoreFields: false,
      hasFields: false,
      hasMore,
      lastCategoryWithFields: null,
      queryVariables: []
    };
  };

  onBulkAssignChange = (categoryId, addedParticipants, removedParticipants) => {
    const { queryVariables } = this.state;
    const filteredQueryVariables = queryVariables.filter(qv => qv.categoryId === categoryId);
    this.addRemoveQuestionParticipants(filteredQueryVariables, addedParticipants, removedParticipants);
  };

  getNextCategory = currentCategory => {
    const { categories } = this.props;
    return categories.find(c => c.order > currentCategory.order) || null;
  };

  // Category was collapsed
  // If it was current category then find next category and load its data
  onStopCategoryAutoLoad = category => {
    let { currentCategory } = this.state;
    if (this.isCurrentCategory(category)) {
      currentCategory = this.getNextCategory(category);
      this.setState({
        currentCategory,
        hasMore: !!currentCategory,
        requestMoreFields: !!currentCategory
      });
    } else {
      this.autoLoad();
    }
  };

  isCurrentCategory = category => {
    const { currentCategory } = this.state;
    return category.id === (currentCategory && currentCategory.id);
  };

  loadCategory = category => {
    const { currentCategory } = this.state;
    return category.order <= currentCategory.order;
  };

  isLastCategoryWithFields = category => {
    const { lastCategoryWithFields } = this.state;
    return category.id === (lastCategoryWithFields && lastCategoryWithFields.id);
  };

  // Track completed requests for all categories
  // Query variables will be used to update local store
  onRequestComplete = (category, hasMore, hasFields, queryVariables) => {
    let { currentCategory, lastCategoryWithFields } = this.state;
    const { categoriesToLoad } = this.state;
    const { selectedCategoryOrder, updateCurrentCategory } = this.props;
    const isCurrent = this.isCurrentCategory(category);
    // Check before updating currentCategory
    if (categoriesToLoad - 1 >= 0) {
      this.setState(prevState => ({
        categoriesToLoad: prevState.categoriesToLoad - 1
      }));
    } else if (categoriesToLoad === 0) {
      this.setState(() => ({
        categoriesToLoad: -1
      }));
      EventEmitter.dispatch(EventEmitterTypes.SHOW_LOADER, { showLoader: false });
      setTimeout(() => {
        EventEmitter.dispatch(EventEmitterTypes.SCROLL_INTO_VIEW, { selectedCategoryOrder });
      }, 100);
    }
    if (hasFields && isCurrent) {
      lastCategoryWithFields = currentCategory;
    }

    if (!hasMore && isCurrent) {
      currentCategory = this.getNextCategory(category);
      if (updateCurrentCategory) {
        updateCurrentCategory(currentCategory);
      }

      this.setState(prevState => ({
        currentCategory,
        hasMore: !!currentCategory,
        hasFields: prevState.hasFields || hasFields,
        lastCategoryWithFields,
        requestMoreFields: false,
        queryVariables: [...prevState.queryVariables, queryVariables]
      }));
    }
  };

  autoLoad = () => {
    const { requestMoreFields, hasMore } = this.state;
    if (!requestMoreFields && hasMore) {
      this.setState({ requestMoreFields: true });
    }
  };

  getUpdatedAssignees = (assessmentParticipants, current, added, removed) => {
    let updatedAssignees = [];

    // Remove assignees
    if (current.length > 0 && removed.length > 0) {
      const keptUsers = current.filter(user => !removed.find(r => r === user.email));
      updatedAssignees = keptUsers;
    } else {
      updatedAssignees = [...current];
    }

    // Add assignees
    if (added.length > 0) {
      const addedUsers = assessmentParticipants.filter(p => added.find(a => a === p.email));
      updatedAssignees = _.unionBy(updatedAssignees, addedUsers, 'email');
    }

    return updatedAssignees;
  };

  updateLocalStore = args => {
    const { client } = this.props;
    return client.writeQuery(args);
  };

  // Update local store when there is a change in participants that impacts any questions
  addRemoveQuestionParticipants(queryVariables, addedParticipants, removedParticipants) {
    const { assessmentParticipants, client } = this.props;

    queryVariables.forEach(variables => {
      const result = client.readQuery({ query: listFieldsForCategory, variables });

      const updatedEdges = result.listFieldsForCategory.edges.map(edge => {
        const current = edge.node.assignees;
        return {
          ...edge,
          node: {
            ...edge.node,
            assignees: this.getUpdatedAssignees(assessmentParticipants, current, addedParticipants, removedParticipants)
          }
        };
      });
      const updatedFieldsForCategory = {
        ...result.listFieldsForCategory,
        edges: updatedEdges
      };
      const data = { listFieldsForCategory: updatedFieldsForCategory };
      // Using defer to give Apollo time to update store
      _.defer(this.updateLocalStore, { query: listFieldsForCategory, variables, data }, 300);
    });
  }

  render() {
    const { t, isNewRecord, setAssigneesForField: propsSetAssigneesForField, currentUser } = this.props;
    const { hasFields, hasMore, requestMoreFields } = this.state;
    if (!hasFields && !hasMore) {
      return <div className="questions questions-empty">{t('assessment.questions.noQuestions')}</div>;
    }
    const {
      assessment,
      categories,
      commentMetadata,
      incrementAttachmentUploadCount,
      decrementAttachmentUploadCount,
      actions,
      selectedCategoryOrder
    } = this.props;
    const isSubmitted = isAssessmentSubmitted(assessment.status);
    const elements = categories.map((category, index) => (
      <Category
        key={category.id}
        category={category}
        actions={actions}
        invokeActionsForFields={actions && actions.invokeActionsForFields ? actions.invokeActionsForFields : []}
        assessment={assessment}
        autoLoadMore={requestMoreFields && this.loadCategory(category)}
        onBulkAssignChange={this.onBulkAssignChange}
        onStopCategoryAutoLoad={this.onStopCategoryAutoLoad}
        onRequestComplete={this.onRequestComplete}
        isLastCategoryWithFields={this.isLastCategoryWithFields(category)}
        setAssigneesForField={propsSetAssigneesForField}
        currentUser={currentUser}
        incrementAttachmentUploadCount={incrementAttachmentUploadCount}
        decrementAttachmentUploadCount={decrementAttachmentUploadCount}
        isNewRecord={isNewRecord}
        categoryRef={this.categoryRefs[index]}
        selectedCategoryOrder={selectedCategoryOrder}
        commentMetadata={commentMetadata}
      />
    ));
    /* eslint-disable react/jsx-wrap-multilines */
    return (
      <div className={`questions ${isSubmitted ? 'submitted' : 'open'}`}>
        <InfiniteScroll
          className="infinite-scroll-items"
          pageStart={0}
          loadMore={this.autoLoad}
          hasMore={hasMore}
          loader={
            <div key={0} className="loader">
              <Loader size="md" color="primary" />
            </div>
          }
          useWindow={false}
        >
          {elements}
        </InfiniteScroll>
      </div>
    );
  }
}

export const QuestionsHOC = compose(
  withTranslation(),
  withApollo,
  withRouter,
  graphql(setAssigneesForField, { name: 'setAssigneesForField' }),
  mapProps(props => {
    const { assessment, actions } = props;
    return {
      client: props.client,
      t: props.t,
      fieldsFilter: getQueryFilterVariables(props),
      assessment,
      actions,
      categories: getObjValue(props, 'assessment.categories', []),
      assessmentParticipants: getObjValue(props, 'assessment.participants', []),
      setAssigneesForField: props.setAssigneesForField,
      currentUser: props.currentUser,
      incrementAttachmentUploadCount: props.incrementAttachmentUploadCount,
      decrementAttachmentUploadCount: props.decrementAttachmentUploadCount,
      isNewRecord: props.isNewRecord,
      selectedCategoryOrder: props.selectedCategoryOrder,
      updateCurrentCategory: props.updateCurrentCategory,
      commentMetadata: props.commentMetadata
    };
  })
);

export default QuestionsHOC(Questions);
